import { Parser as N3Parser } from "n3";
import type { DatasetDescriptor } from "../rdf/types";

/**
 * Persistence + descriptor mapping for user-uploaded ontologies.
 *
 * Uploaded Turtle files are stored whole in IndexedDB (localStorage's ~5MB
 * quota is too small for real ontology files) and surfaced to the rest of
 * the app as ordinary `DatasetDescriptor`s with `origin: "user"` and their
 * content inlined into the version's file entry — the store loader, docs
 * pages, search, and visualizer all treat them exactly like bundled
 * datasets from that point on.
 */

export interface StoredUserOntology {
  id: string;
  label: string;
  fileName: string;
  text: string;
  accentColor: string;
  /** ISO timestamp of the upload. */
  uploadedAt: string;
  /** Best-guess primary namespace, derived from the most common subject namespace at upload time. */
  namespaceIri: string;
  tripleCount: number;
}

const DB_NAME = "ontology-explorer";
const DB_VERSION = 1;
const STORE_NAME = "user-ontologies";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const request = run(tx.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
    });
  } finally {
    db.close();
  }
}

export async function listUserOntologies(): Promise<StoredUserOntology[]> {
  const all = await withStore<StoredUserOntology[]>("readonly", (store) => store.getAll());
  return all.sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
}

export async function saveUserOntology(ontology: StoredUserOntology): Promise<void> {
  await withStore("readwrite", (store) => store.put(ontology));
}

export async function deleteUserOntology(id: string): Promise<void> {
  await withStore("readwrite", (store) => store.delete(id));
}

/** Accent colors cycled through for uploads — distinct from the bundled datasets' hues where possible. */
const USER_ACCENT_COLORS = ["#14b8a6", "#f43f5e", "#8b5cf6", "#f59e0b", "#0ea5e9", "#d946ef"];

export function nextUserAccentColor(existingCount: number): string {
  return USER_ACCENT_COLORS[existingCount % USER_ACCENT_COLORS.length]!;
}

export interface ValidatedUpload {
  tripleCount: number;
  namespaceIri: string;
  /** Ontology label from the file's own owl:Ontology declaration, if present. */
  selfLabel: string | null;
}

const RDFS_LABEL = "http://www.w3.org/2000/01/rdf-schema#label";
const DCTERMS_TITLE = "http://purl.org/dc/terms/title";
const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const OWL_ONTOLOGY = "http://www.w3.org/2002/07/owl#Ontology";

function namespaceOf(iri: string): string {
  const hash = iri.lastIndexOf("#");
  if (hash !== -1) return iri.slice(0, hash + 1);
  const slash = iri.lastIndexOf("/");
  if (slash !== -1) return iri.slice(0, slash + 1);
  return iri;
}

/**
 * Parses uploaded Turtle text up-front so the upload dialog can reject
 * malformed files with the parser's actual error message (line/column
 * included) instead of failing later during a store rebuild, and so the
 * descriptor can carry a namespace guess + triple count.
 */
export function validateTurtle(text: string): ValidatedUpload {
  const parser = new N3Parser({ format: "Turtle" });
  const quads = parser.parse(text);
  if (quads.length === 0) {
    throw new Error("File parsed but contains no triples.");
  }

  const namespaceCounts = new Map<string, number>();
  let ontologyIri: string | null = null;
  for (const quad of quads) {
    if (quad.subject.termType === "NamedNode") {
      const ns = namespaceOf(quad.subject.value);
      namespaceCounts.set(ns, (namespaceCounts.get(ns) ?? 0) + 1);
      if (
        quad.predicate.value === RDF_TYPE &&
        quad.object.termType === "NamedNode" &&
        quad.object.value === OWL_ONTOLOGY
      ) {
        ontologyIri = quad.subject.value;
      }
    }
  }

  let selfLabel: string | null = null;
  if (ontologyIri) {
    for (const quad of quads) {
      if (
        quad.subject.termType === "NamedNode" &&
        quad.subject.value === ontologyIri &&
        (quad.predicate.value === RDFS_LABEL || quad.predicate.value === DCTERMS_TITLE) &&
        quad.object.termType === "Literal"
      ) {
        selfLabel = quad.object.value;
        break;
      }
    }
  }

  const topNamespace =
    [...namespaceCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";

  return { tripleCount: quads.length, namespaceIri: topNamespace, selfLabel };
}

export function toDescriptor(stored: StoredUserOntology): DatasetDescriptor {
  const uploadedDate = stored.uploadedAt.slice(0, 10);
  return {
    id: stored.id,
    label: stored.label,
    shortLabel: stored.label.length > 18 ? `${stored.label.slice(0, 16)}…` : stored.label,
    description: `Uploaded from ${stored.fileName} · ${stored.tripleCount.toLocaleString()} triples`,
    namespaceIri: stored.namespaceIri,
    sourceUrl: "",
    license: "User-provided",
    accentColor: stored.accentColor,
    origin: "user",
    enabledByDefault: true,
    versions: [
      {
        id: "uploaded",
        label: `Uploaded ${uploadedDate}`,
        releaseDate: uploadedDate,
        note: "User upload — the only version of this ontology in the workspace",
        files: [
          {
            id: `${stored.id}-file`,
            fileName: stored.fileName,
            path: stored.fileName,
            format: "turtle",
            text: stored.text,
          },
        ],
      },
    ],
  };
}
